import React, { useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthDebugPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [authState, setAuthState] = useState('Unknown');

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${timestamp}] ${message}`);
  };

  useEffect(() => {
    addLog('AuthDebugPage mounted');
    addLog(`Current URL: ${window.location.href}`);
    addLog(`Auth domain: ${auth.app.options.authDomain}`);
    addLog(`Project ID: ${auth.app.options.projectId}`);

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      addLog(`Auth state changed: ${currentUser ? 'Signed In' : 'Signed Out'}`);
      setUser(currentUser);
      setAuthState(currentUser ? 'Signed In' : 'Signed Out');
      
      if (currentUser) {
        addLog(`User: ${currentUser.email} (${currentUser.uid})`);
        addLog(`Provider: ${currentUser.providerData[0]?.providerId || 'Unknown'}`);
      }
    });

    // Check for redirect result on mount
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          addLog('Redirect result received', 'success');
          addLog(`User from redirect: ${result.user.email}`);
        } else {
          addLog('No redirect result');
        }
      })
      .catch((error) => {
        addLog(`Redirect result error: ${error.code} - ${error.message}`, 'error');
      });

    return () => unsubscribe();
  }, []);

  const testPopupLogin = async () => {
    setLoading(true);
    addLog('Starting popup login test...');
    
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      addLog('Creating popup...');
      const result = await signInWithPopup(auth, provider);
      addLog('Popup login successful!', 'success');
      addLog(`Authenticated user: ${result.user.email}`, 'success');
    } catch (error) {
      addLog(`Popup login failed: ${error.code}`, 'error');
      addLog(`Error message: ${error.message}`, 'error');
      addLog(`Full error: ${JSON.stringify(error, null, 2)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const testRedirectLogin = async () => {
    addLog('Starting redirect login test...');
    
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    try {
      addLog('Initiating redirect...');
      await signInWithRedirect(auth, provider);
    } catch (error) {
      addLog(`Redirect login failed: ${error.code}`, 'error');
      addLog(`Error message: ${error.message}`, 'error');
    }
  };

  const testLogout = async () => {
    try {
      await signOut(auth);
      addLog('Logout successful', 'success');
    } catch (error) {
      addLog(`Logout failed: ${error.code}`, 'error');
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 mb-6">
          <h1 className="text-3xl font-bold text-white mb-6">Firebase Auth Debug</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white/5 rounded-lg p-4">
              <h2 className="text-xl font-semibold text-white mb-2">Auth State</h2>
              <p className="text-white/80">Status: {authState}</p>
              {user && (
                <div className="mt-2 text-sm text-white/70">
                  <p>Email: {user.email}</p>
                  <p>UID: {user.uid}</p>
                  <p>Provider: {user.providerData[0]?.providerId}</p>
                </div>
              )}
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <h2 className="text-xl font-semibold text-white mb-2">Environment</h2>
              <div className="text-sm text-white/70 space-y-1">
                <p>URL: {window.location.href}</p>
                <p>Auth Domain: {auth.app.options.authDomain}</p>
                <p>Project: {auth.app.options.projectId}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex flex-wrap gap-4">
              <button
                onClick={testPopupLogin}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg transition-all"
              >
                {loading ? 'Testing...' : 'Test Popup Login'}
              </button>
              
              <button
                onClick={testRedirectLogin}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-all"
              >
                Test Redirect Login
              </button>
              
              {user && (
                <button
                  onClick={testLogout}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-all"
                >
                  Test Logout
                </button>
              )}
              
              <button
                onClick={clearLogs}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-all"
              >
                Clear Logs
              </button>
            </div>
          </div>

          <div className="bg-black/20 rounded-lg p-4 max-h-96 overflow-y-auto">
            <h3 className="text-lg font-semibold text-white mb-3">Debug Logs</h3>
            <div className="space-y-1 text-sm font-mono">
              {logs.map((log, index) => (
                <div key={index} className={`
                  ${log.type === 'error' ? 'text-red-300' : 
                    log.type === 'success' ? 'text-green-300' : 'text-white/80'}
                `}>
                  <span className="text-white/50">[{log.timestamp}]</span> {log.message}
                </div>
              ))}
              {logs.length === 0 && (
                <p className="text-white/50">No logs yet...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthDebugPage;
